import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { msg } from '@lingui/core/macro';
import * as Sentry from '@sentry/tanstackstart-react';
import { createIsomorphicFn } from '@tanstack/react-start';
import { getRequestHeaders, setResponseStatus } from '@tanstack/react-start/server';
import { OperationDefinitionNode, print } from 'graphql';

import { getClientTag } from '#features/app-update/utils/appVersion';
import { CLIENT_OUTDATED_CODE, isSchemaDriftError } from '#features/app-update/utils/schemaDrift';
import { reportServerClosed } from '#features/app-update/utils/statusFlags';
import { noteServerDate } from '#features/date/utils/serverClock';
import { defaultLocale } from '#features/i18n/constants/locales';
import { getLocaleFromRequest } from '#features/i18n/server';
import { getLocaleFromPath } from '#features/i18n/utils';
import { getActiveI18n } from '#features/i18n/utils/getActiveI18n';
import { markNetworkFailure, markNetworkProven } from '#features/network/model/connectivity';
import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';
import { ApiError } from './ApiError';
import { getAppCheckToken, isIOSApp } from './appCheck';
import { rememberBanNotice } from './banNotice';
import { CAPTCHA_ERROR_CODE, requiresCaptcha } from './captchaOperations';
import { getNativeToken } from './nativeAuth';

export const USER_NOT_AUTH_CODE = 'user.not.auth';
const USER_BANNED_CODE = 'user.banned';
const PERMISSION_DENIED_CODE = 'permission.denied';

/**
 * Падение бэкенда на SSR обязано стать 503 у самого документа — тогда nginx подменит страницу
 * на maintenance.html. No-op на клиенте (там ответ уже отдан).
 */
const markSsrResponseUnavailable = createIsomorphicFn()
  .client(() => {})
  .server(() => setResponseStatus(503));

/**
 * Адрес человека, за которого сейчас ходит SSR.
 *
 * Серверная отрисовка идёт из нашего контейнера, поэтому все её запросы приходят в шлюз с одного
 * адреса. Любой лимит по IP при этом считает не людей, а нас самих: шестьдесят открытий
 * страницы-приглашения подряд глушили публичный лендинг всем сразу. Заголовок клиента передаём
 * дальше — за Cloudflare это `CF-Connecting-IP`, без неё `X-Real-IP` от nginx.
 *
 * На клиенте no-op: там заголовок ставит сам прокси, а подделать его из браузера нельзя и не нужно.
 */
const getForwardedClientIp = createIsomorphicFn()
  .client((): string | undefined => undefined)
  .server((): string | undefined => {
    try {
      const headers = getRequestHeaders();
      return headers.get('cf-connecting-ip') ?? headers.get('x-real-ip') ?? undefined;
    } catch {
      // Вне запроса заголовков нет: прогрев, фоновая задача, тест. Адрес просто не передаём.
      return undefined;
    }
  });

const getHeaders = async (
  withAppCheck: boolean
): Promise<Record<string, string | null | undefined>> => {
  const reCaptchaToken = localStorage.getItem('reCaptchaToken');
  let locale = defaultLocale;

  if (envConfig.IN_SERVER) {
    // The URL locale overrides the incoming cookie, including a first visit with no cookie.
    locale = getLocaleFromRequest();
  } else {
    locale = getLocaleFromPath();
  }

  // Native shell has no auth cookie — send the JWT in the `Accesstoken` header
  // instead (that's the app's configured app.jwt.header-key; the gateway reads it
  // as a fallback after the cookie). Web keeps using the cookie and never sets
  // this. No-op / dead-code-eliminated on web.
  const nativeToken = await getNativeToken();

  // Подтверждение подлинности приложения вместо капчи: в WebView reCAPTCHA всегда видит бота,
  // поэтому шлюз, получив валидный App Check, капчу не спрашивает. В вебе всегда null и заголовок
  // не уходит (см. appCheck.ts).
  //
  // Только для операций с капчей: остальным шлюз токен не проверяет, а ожидание аттестации
  // (2–4 с на холодном старте) держало пустой ленту приложения. Список – captchaOperations.ts.
  const appCheckToken = withAppCheck ? await getAppCheckToken() : null;

  // Пусто на клиенте и почти всегда заполнено на SSR — см. getForwardedClientIp.
  const clientIp = envConfig.IN_SERVER ? getForwardedClientIp() : undefined;

  return {
    'tm-captcha': reCaptchaToken,
    ...(appCheckToken && { 'tm-app-check': appCheckToken }),
    // Policy selector, not proof of identity. The gateway requires a verified iOS App Check
    // token on protected operations, even on dev and even when the header has no token.
    ...(isIOSApp() && { 'tm-native-platform': 'ios' }),
    ...(envConfig.IN_SERVER && {
      'Accept-Language': 'ru-RU,ru;q=0.9,en-US;q=0.8,en;q=0.7',
      ...(clientIp && { 'CF-Connecting-IP': clientIp }),
    }),
    ...(nativeToken && { Accesstoken: nativeToken }),
    'active-tab': (envConfig.IN_SERVER ? false : document.visibilityState === 'visible').toString(),
    // Версия клиента в каждом запросе. Раньше она доезжала до бэкенда единственным путём —
    // вместе с регистрацией push-токена, то есть меньше чем у 2% устройств и только на Android;
    // разложить аварию по версиям было нечем. Пишется в access-лог nginx ($http_tm_client).
    // Заголовок обязан быть в allowedHeaders шлюза, иначе cross-origin preflight его отвергнет.
    'tm-client': getClientTag(),
    Language: locale,
  };
};

/**
 * Сколько чтение ждёт ответа, прежде чем считать его обрывом.
 *
 * В приложении запросы идут через нативный HTTP Capacitor, а он не знает ни `AbortSignal`, ни
 * таймаутов. Пока на старте включался VPN, такие запросы висели по 40-70 с и кончались
 * "unexpected end of stream", лента стояла скелетом 150 с, а свежий запрос в то же время
 * отвечал за полсекунды (замер на телефоне 16.09.2026). Через 20 с чтение считается сетевым
 * сбоем (статус 0): react-query повторит его новым запросом, а сбой засчитается в "сервер
 * недоступен". Брошенный нативный запрос дорабатывает сам, его ответ никуда не идёт.
 *
 * Только чтения и только в браузере. Мутацию так не бросить: она могла дойти, повтора у неё нет
 * нарочно, и второй запрос создал бы дубль. Рендер на сервере живёт по своим таймаутам.
 */
export const READ_TIMEOUT_MS = 20_000;

const READ_TIMEOUT_ERROR = 'TimeoutError';

const isReadOperation = (
  documentNode: string | undefined,
  definition: OperationDefinitionNode | undefined
): boolean => {
  if (definition) {
    return definition.operation === 'query';
  }
  return typeof documentNode === 'string' && /^\s*(query\b|\{)/.test(documentNode);
};

/**
 * Ответ или отказ через `timeoutMs`. Поздний ответ (или отказ) запроса гасит сама гонка: она
 * уже подписана на него, необработанного отказа не будет.
 */
const withTimeout = <T>(request: Promise<T>, timeoutMs: number, onTimeout: () => void) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      const error = new Error(`No answer in ${timeoutMs} ms`);
      error.name = READ_TIMEOUT_ERROR;
      // Сначала отказ по таймауту, потом отмена: иначе гонку выиграл бы `AbortError` отменённого
      // запроса, а его fetcher пробрасывает как есть, без повтора.
      reject(error);
      onTimeout();
    }, timeoutMs);
  });
  return Promise.race([request, timeout]).finally(() => clearTimeout(timer));
};

export const gqlFetcher = async <TData, TVariables = unknown>(
  documentNode: TypedDocumentNode<TData, TVariables> | string,
  variables?: TVariables | null,
  options?: {
    url?: string;
    revalidate?: number | false;
    tags?: string[];
    headers?: Record<string, string | null>;
    /**
     * Пережить выгрузку страницы. Нужен ровно там, где запрос отправляют в `pagehide`:
     * обычный fetch браузер отменяет вместе с документом, и телеметрия закрытой вкладки —
     * то есть самого интересного сеанса — теряется целиком.
     */
    keepalive?: boolean;
    /**
     * Не шуметь в Sentry и в консоли. Для запросов, которые никто не ждёт и которые по природе
     * своей падают на плохой сети — телеметрия при закрытии вкладки. Каждое такое падение в
     * Sentry — это шум, скрывающий настоящие ошибки.
     */
    silent?: boolean;
    /**
     * Приложить токен App Check, даже если документа нет в списке операций с капчей. Ставит сам
     * fetcher, повторяя запрос после `captcha.error` (см. ниже); снаружи не нужен.
     */
    forceAppCheck?: boolean;
    /** Native connect/read inactivity timeouts, including response data. Serialized push
     * mutations on logout cancel in URLSession/OkHttp instead of abandoning a live promise. */
    nativeTimeoutMs?: number;
    /**
     * Ждать ответ не дольше этого срока и считать запрос не дошедшим (ошибка сети, статус 0).
     *
     * Чтения таймаут получают сами (`READ_TIMEOUT_MS`), мутации - нет: она могла дойти, и повтор
     * создал бы дубль. Но молча крутить кнопку «Вступить» на мёртвой сети до тайм-аута браузера
     * (минуты) тоже нельзя, поэтому действия, которым нужен ответ «прошло или нет», просят срок
     * явно. Брошенная мутация всё равно может выполниться на сервере, поэтому вызывающий после
     * такого отказа перечитывает состояние, а не угадывает его.
     */
    timeoutMs?: number;
  }
): Promise<TData> => {
  const withAppCheck =
    envConfig.IS_NATIVE && (options?.forceAppCheck === true || requiresCaptcha(documentNode));
  const headers = await getHeaders(withAppCheck);
  const requestUrl =
    (options?.url ?? (envConfig.IN_DEV && envConfig.IN_BROWSER)) ? '/graphql' : envConfig.API_URL;
  const operationDefinition =
    typeof documentNode === 'string'
      ? undefined
      : documentNode.definitions?.find(
          (definition): definition is OperationDefinitionNode =>
            definition.kind === 'OperationDefinition'
        );
  const operationName = operationDefinition?.name?.value ?? 'anonymous';
  const query =
    typeof documentNode === 'string'
      ? documentNode
      : (documentNode.loc?.source?.body ?? print(documentNode));
  const requestStartedAt = Date.now();
  const timesOut =
    envConfig.IN_BROWSER &&
    isReadOperation(
      typeof documentNode === 'string' ? documentNode : undefined,
      operationDefinition
    );
  // В вебе брошенный запрос ещё и отменяется; нативный HTTP сигнал не слушает.
  const timeoutMs = options?.timeoutMs ?? READ_TIMEOUT_MS;
  const abort = timesOut || options?.timeoutMs ? new AbortController() : null;
  let res: Response;

  try {
    const requestHeaders = {
      'Content-Type': 'application/json',
      ...headers,
      ...options?.headers,
    };
    const requestBody = JSON.stringify({ query, variables });
    const request =
      envConfig.IS_NATIVE && options?.nativeTimeoutMs
        ? (async () => {
            const { CapacitorHttp } = await import('@capacitor/core');
            const nativeResponse = await CapacitorHttp.request({
              url: new URL(requestUrl, envConfig.API_URL).href,
              method: 'POST',
              headers: Object.fromEntries(
                Object.entries(requestHeaders).filter(
                  (entry): entry is [string, string] => typeof entry[1] === 'string'
                )
              ),
              data: requestBody,
              responseType: 'text',
              connectTimeout: options.nativeTimeoutMs,
              readTimeout: options.nativeTimeoutMs,
            });
            // Capacitor resolves only after the complete body. The shared GraphQL error/App
            // Check handling below therefore stays identical to ordinary requests.
            return new Response(
              typeof nativeResponse.data === 'string'
                ? nativeResponse.data
                : JSON.stringify(nativeResponse.data),
              { status: nativeResponse.status, headers: nativeResponse.headers }
            );
          })()
        : fetch(requestUrl, {
            method: 'POST',
            headers: requestHeaders,
            credentials: 'include',
            ...(options?.keepalive ? { keepalive: true } : {}),
            ...(abort ? { signal: abort.signal } : {}),
            body: requestBody,
          });
    res = await (abort ? withTimeout(request, timeoutMs, () => abort.abort()) : request);
    noteServerDate(res.headers?.get('date'), requestStartedAt, Date.now());
  } catch (error) {
    const durationMs = Date.now() - requestStartedAt;

    Sentry.withScope((scope) => {
      scope.setTag('scope', 'gqlFetcher');
      scope.setTag('type', 'network');
      scope.setTag('graphql.operation', operationName);
      if (error instanceof Error && error.name === READ_TIMEOUT_ERROR) {
        scope.setTag('network.timeout', 'true');
      }
      scope.setLevel('warning');
      scope.setContext('network', {
        url: requestUrl,
        method: 'POST',
        durationMs,
        online: envConfig.IN_BROWSER ? navigator.onLine : undefined,
        visibilityState: envConfig.IN_BROWSER ? document.visibilityState : undefined,
      });
      scope.setContext('graphql', {
        operationName,
      });
      if (!options?.silent) {
        Sentry.captureException(error);
      }
    });

    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }

    /*
      Ответа нет вовсе - шаг к "сервер недоступен" (см. `markNetworkFailure`). На сервере
      рендера это не про устройство человека, и общий на все запросы счёт там не ведётся.

      Таймаут передаём отдельно: он значит "не успел", а не "не отвечает", и к недоступности сам
      по себе не ведёт - решает перепроверка связи.
    */
    if (envConfig.IN_BROWSER) {
      const timedOut = error instanceof Error && error.name === READ_TIMEOUT_ERROR;
      markNetworkFailure(requestStartedAt, timedOut);
    }

    const isOffline = envConfig.IN_BROWSER && navigator.onLine === false;
    const i18n = await getActiveI18n();
    const offlineMessage = i18n._(
      msg({
        id: 'common:network.offline',
        message: 'No internet connection. Please check your network and try again.',
      })
    );
    const networkMessage = i18n._(
      msg({
        id: 'common:network.error',
        message: 'Network error. Please check your connection and try again.',
      })
    );
    throw new ApiError(isOffline ? offlineMessage : networkMessage, 0);
  }

  /*
    🔴 «Связь есть» засчитывается не здесь, а после того, как тело действительно прочитано.

    Промис `fetch` разрешается по ЗАГОЛОВКАМ, и на этом же месте гонка с таймаутом снимала свой
    таймер (`withTimeout` чистит его в `finally`). То есть дальше тело читалось без всякого
    предела: шлюз, отдавший заголовки и замолчавший на теле, считался живым и отвечающим, полоса
    «нет связи» гасла, а чтение висело столько, сколько держится соединение.

    Ранние ветки ниже (401/403 и 502/503/504) тела не читают, и для них ответ доказан уже сейчас -
    там `markNetworkProven` зовётся отдельно. Иначе они перестали бы снимать паузу запросов, и
    вернулась бы ровно та петля, которую чинила проверка достижимости.
  */
  const proven = () => {
    if (envConfig.IN_BROWSER) {
      markNetworkProven();
    }
  };

  // A bare 401/403 never comes from our gateway: it answers auth failures with 200 and an
  // error in the body (see USER_NOT_AUTH_CODE below). So this is something in front of us —
  // a bot challenge, a WAF rule, a proxy — and it says nothing about the token. Left
  // untagged on purpose: callers must not treat it as a reason to sign the person out.
  if (res.status === 401 || res.status === 403) {
    proven();
    throw new ApiError('Unauthorized', res.status);
  }

  // 502/504 = short upstream/CF blip, body is HTML — don't try to parse as JSON.
  // 503 = our nginx @graphql_maintenance (JSON body), thrown with same message so
  // queryClient retries all three transparently.
  if (res.status === 502 || res.status === 503 || res.status === 504) {
    // Первым делом: ниже есть шаги, которые на сервере рендера могут бросить сами, и тогда до
    // конца ветки дело не дойдёт - а ответ от шлюза уже получен, связь доказана.
    proven();
    // On the server, a down/updating backend must surface as a 503 on the SSR
    // response itself, so nginx (@frontend_maintenance) swaps the page for
    // maintenance.html. Set it in the loader phase, before the stream starts.
    // The thrown ApiError is preserved so the client can still retry transparently.
    markSsrResponseUnavailable();
    // 503 is the one status that means "we closed the door", not "something blipped": it comes
    // from our own nginx under the maintenance flag. The app polls /app/status.json rarely — at
    // launch and on return from background — so whoever is already inside would keep tapping
    // into errors until they restart it. Tell the version gate to go and look now; the gate
    // still decides on the status file, not on this signal.
    if (res.status === 503) {
      reportServerClosed();
    }
    const i18n = await getActiveI18n();
    const maintenanceMessage = i18n._(
      msg({
        id: 'common:server.maintenance',
        message: 'Server is updating. Please try again in a moment.',
      })
    );
    throw new ApiError(maintenanceMessage, res.status);
  }

  /*
    Остаток общего срока на чтение тела. Дедлайн один на запрос: ответ, начавшийся вовремя, но не
    доехавший, - это тот же таймаут, и решать его должна та же перепроверка связи.

    Обрыв тела и негодный JSON тоже здесь: раньше они улетали наружу как есть - не превращались
    в `ApiError` со статусом 0, не считались сетевым провалом и не давали повтора, хотя
    `queryClient` повторяет ровно такие.
  */
  let body: unknown;
  try {
    const left = abort ? Math.max(timeoutMs - (Date.now() - requestStartedAt), 1) : 0;
    body = await (abort ? withTimeout(res.json(), left, () => abort.abort()) : res.json());
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    if (envConfig.IN_BROWSER) {
      const timedOut = error instanceof Error && error.name === READ_TIMEOUT_ERROR;
      markNetworkFailure(requestStartedAt, timedOut);
    }
    Sentry.withScope((scope) => {
      scope.setTag('scope', 'gqlFetcher');
      scope.setTag('type', 'body');
      scope.setTag('graphql.operation', operationName);
      Sentry.captureException(error);
    });
    const i18n = await getActiveI18n();
    throw new ApiError(
      i18n._(
        msg({
          id: 'common:network.error',
          message: 'Network error. Please check your connection and try again.',
        })
      ),
      0
    );
  }

  // Тело прочитано целиком - вот теперь ответ доказан.
  proven();

  const data = body as {
    data: TData;
    errors?: {
      message: string;
      path?: (string | number)[];
      extensions?: {
        httpCode?: number;
        errorCode?: string;
        code?: string;
        /** graphql-java: ValidationError / InvalidSyntax / DataFetchingException / … */
        classification?: string;
      };
    }[];
  };

  if (data.errors?.length) {
    if (!options?.silent) {
      console.error(data.errors);
    }
    const error = data.errors[0];
    const errorCode = error?.extensions?.errorCode ?? error?.extensions?.code;

    // Страховка списка операций с капчей. Шлюз отбил капчу на операции, которой в списке нет,
    // значит на её поле появилась проверка, а в captchaOperations.ts её не внесли. reCAPTCHA в
    // WebView не помогла (там она всегда видит бота), поэтому сообщаем о дыре в Sentry и один раз
    // повторяем с токеном App Check. Капча проверяется при разборе аргументов, до резолвера, так
    // что отбитая операция ничего не успела сделать и повтор её не задвоит.
    if (envConfig.IS_NATIVE && !withAppCheck && errorCode === CAPTCHA_ERROR_CODE) {
      Sentry.withScope((scope) => {
        scope.setTag('scope', 'gqlFetcher');
        scope.setTag('graphql.operation', operationName);
        scope.setLevel('warning');
        Sentry.captureMessage('captcha operation missing from CAPTCHA_FIELD_PATHS');
      });

      return gqlFetcher(documentNode, variables, { ...options, forceAppCheck: true });
    }

    // Документ не сошёлся со схемой шлюза: отклонён ЦЕЛИКОМ, ещё до резолверов, с HTTP 200 и
    // без `errorCode`. Раньше это доходило до общего throw ниже и человек видел безликое
    // «что-то пошло не так» — ровно так выглядела авария versionCode 11. Помечаем отдельным
    // кодом; что именно показать (обновиться или «попробуйте позже»), решает queryClient по
    // версии из /app/status.json, а не по одному этому признаку.
    if (isSchemaDriftError(error)) {
      throw new ApiError(error?.message ?? 'Client out of date', res.status, CLIENT_OUTDATED_CODE);
    }

    // Partial success: the operation resolved, but a nested nullable auth-gated field
    // (e.g. discordVoiceChannel.realtimeMembers) came back `user.not.auth`. GraphQL nulls
    // just that field and still fills the rest — so don't throw a fully valid post away
    // over a sub-field the caller already treats as optional. Scoped tight: only when data
    // is present AND every error is a not-logged-in error on a nested field (path depth ≥ 3
    // in this API's `wrapper { resolver { field } }` shape). A root-level auth failure
    // (shorter path or null data) still throws below, so login redirects are unaffected;
    // banned / permission / other errors are never swallowed.
    const isRecoverableFieldAuthMiss = (candidate: NonNullable<typeof data.errors>[number]) => {
      const code = candidate?.extensions?.errorCode ?? candidate?.extensions?.code;
      const isNestedField = (candidate?.path?.length ?? 0) >= 3;
      return (
        isNestedField && (code === USER_NOT_AUTH_CODE || candidate?.extensions?.httpCode === 401)
      );
    };
    if (data.data != null && data.errors.every(isRecoverableFieldAuthMiss)) {
      return data.data;
    }

    if (error?.extensions?.httpCode === 401 || errorCode === USER_NOT_AUTH_CODE) {
      // Tagged so callers can tell "our gateway says this token is no good" apart from a
      // bare 401/403 that some layer in front of us produced — see the throw below.
      throw new ApiError('Unauthorized', 401, USER_NOT_AUTH_CODE);
    }

    if (error && errorCode === USER_BANNED_CODE) {
      // The gateway has already reset the auth cookie; do a full-page redirect so the
      // app restarts in the anonymous state on the static /banned page.
      if (envConfig.IN_BROWSER) {
        // Срок и причина приезжают в самом отказе: другого случая их узнать не будет —
        // страница /banned открывается анонимной и спросить ей уже не у кого.
        rememberBanNotice(error.extensions as Record<string, unknown> | undefined);
        window.location.href = '/banned';
      }

      throw new ApiError(error.message, 403, errorCode);
    }

    if (error?.extensions?.httpCode === 403 || errorCode === PERMISSION_DENIED_CODE) {
      throw new ApiError('Access forbidden', res.status);
    }

    if (error) {
      throw new ApiError(error.message, res.status, errorCode);
    }
  }

  return data.data;
};
